export type WebSocketStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed';

export interface WebSocketStatusChange {
  previousStatus: WebSocketStatus;
  status: WebSocketStatus;
}

export interface WebSocketReconnectInfo {
  attempt: number;
  delay: number;
}

export interface WebSocketClientEventMap<IncomingMessage = unknown> {
  close: CloseEvent;
  error: Event;
  heartbeatTimeout: void;
  message: IncomingMessage;
  open: Event;
  rawMessage: MessageEvent;
  reconnect: WebSocketReconnectInfo;
  status: WebSocketStatusChange;
}

export type WebSocketEventName = keyof WebSocketClientEventMap<any>;
export type WebSocketListener<T = any> = (payload: T) => void;

export interface WebSocketClientOptions<
  IncomingMessage = unknown,
  OutgoingMessage = unknown
> {
  autoConnect?: boolean;
  connectTimeout?: number;
  debug?: boolean;
  getUrl?: () => Promise<string> | string;
  heartbeat?: boolean;
  heartbeatInterval?: number;
  heartbeatMessage?: OutgoingMessage | (() => OutgoingMessage);
  heartbeatTimeout?: number;
  maxQueueSize?: number;
  maxReconnectAttempts?: number;
  maxReconnectInterval?: number;
  parser?: (event: MessageEvent) => IncomingMessage;
  protocols?: string | string[];
  queueMessages?: boolean;
  reconnect?: boolean;
  reconnectDecay?: number;
  reconnectInterval?: number;
  reconnectJitter?: number;
  serializer?: (
    message: OutgoingMessage
  ) => string | ArrayBufferLike | Blob | ArrayBufferView;
  shouldReconnect?: (event?: CloseEvent) => boolean;
  url?: string;
}

const DEFAULT_OPTIONS: Required<
  Pick<
    WebSocketClientOptions,
    | 'connectTimeout'
    | 'heartbeat'
    | 'heartbeatInterval'
    | 'heartbeatTimeout'
    | 'maxQueueSize'
    | 'maxReconnectAttempts'
    | 'maxReconnectInterval'
    | 'queueMessages'
    | 'reconnect'
    | 'reconnectDecay'
    | 'reconnectInterval'
    | 'reconnectJitter'
  >
> = {
  connectTimeout: 10000,
  heartbeat: true,
  heartbeatInterval: 25000,
  heartbeatTimeout: 10000,
  maxQueueSize: 100,
  maxReconnectAttempts: Infinity,
  maxReconnectInterval: 30000,
  queueMessages: true,
  reconnect: true,
  reconnectDecay: 1.6,
  reconnectInterval: 1000,
  reconnectJitter: 0.2,
};

const isBrowserRuntime = () =>
  typeof window !== 'undefined' && typeof window.WebSocket !== 'undefined';

const defaultParser = <IncomingMessage>(event: MessageEvent): IncomingMessage => {
  if (typeof event.data !== 'string') {
    return event.data as IncomingMessage;
  }

  try {
    return JSON.parse(event.data) as IncomingMessage;
  } catch {
    return event.data as IncomingMessage;
  }
};

const defaultSerializer = <OutgoingMessage>(
  message: OutgoingMessage
): string | ArrayBufferLike | Blob | ArrayBufferView => {
  if (
    typeof message === 'string' ||
    message instanceof Blob ||
    message instanceof ArrayBuffer ||
    ArrayBuffer.isView(message)
  ) {
    return message;
  }

  return JSON.stringify(message);
};

export class WebSocketClient<
  IncomingMessage = unknown,
  OutgoingMessage = unknown
> {
  private options: WebSocketClientOptions<IncomingMessage, OutgoingMessage>;
  private socket: WebSocket | null = null;
  private status: WebSocketStatus = 'idle';
  private listeners = new Map<WebSocketEventName, Set<WebSocketListener>>();
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private messageQueue: OutgoingMessage[] = [];
  private closedByUser = false;
  private isOnline = true;

  constructor(options: WebSocketClientOptions<IncomingMessage, OutgoingMessage>) {
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options,
    };
    this.bindNetworkEvents();

    if (this.options.autoConnect) {
      this.connect();
    }
  }

  configure(options: WebSocketClientOptions<IncomingMessage, OutgoingMessage>) {
    this.options = {
      ...this.options,
      ...options,
    };
  }

  async connect() {
    if (!isBrowserRuntime()) return;
    if (this.status === 'connecting' || this.status === 'open') return;
    if (!this.isOnline) return;

    const url = await this.resolveUrl();
    if (!url) {
      this.log('WebSocket url is empty, connect skipped.');
      return;
    }

    this.closedByUser = false;
    this.clearReconnectTimer();
    this.setStatus(this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');

    const socket = new WebSocket(url, this.options.protocols);
    this.socket = socket;
    this.startConnectTimeout(socket);

    socket.onopen = event => {
      if (socket !== this.socket) return;

      this.clearConnectTimeout();
      this.reconnectAttempts = 0;
      this.setStatus('open');
      this.emit('open', event);
      this.flushQueue();
      this.startHeartbeat();
    };

    socket.onmessage = event => {
      if (socket !== this.socket) return;

      this.resetHeartbeatTimeout();
      this.emit('rawMessage', event);
      this.emit('message', this.parseMessage(event));
    };

    socket.onerror = event => {
      if (socket !== this.socket) return;

      this.emit('error', event);
    };

    socket.onclose = event => {
      if (socket !== this.socket) return;

      this.clearTimers();
      this.socket = null;
      this.emit('close', event);

      if (this.shouldReconnect(event)) {
        this.scheduleReconnect(event);
        return;
      }

      this.setStatus('closed');
    };
  }

  close(code?: number, reason?: string) {
    this.closedByUser = true;
    this.clearTimers();
    this.clearReconnectTimer();

    if (this.socket) {
      this.socket.close(code, reason);
      this.socket = null;
    }

    this.setStatus('closed');
  }

  reconnect() {
    this.closedByUser = false;
    this.clearReconnectTimer();

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.connect();
  }

  send(message: OutgoingMessage, options: { queue?: boolean } = {}) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(this.serializeMessage(message));
      return true;
    }

    const shouldQueue = options.queue ?? this.options.queueMessages;
    if (!shouldQueue) return false;

    this.enqueueMessage(message);
    return false;
  }

  on<EventName extends WebSocketEventName>(
    eventName: EventName,
    listener: WebSocketListener<WebSocketClientEventMap<IncomingMessage>[EventName]>
  ) {
    const listeners = this.listeners.get(eventName) || new Set();
    listeners.add(listener as WebSocketListener);
    this.listeners.set(eventName, listeners);

    return () => this.off(eventName, listener);
  }

  once<EventName extends WebSocketEventName>(
    eventName: EventName,
    listener: WebSocketListener<WebSocketClientEventMap<IncomingMessage>[EventName]>
  ) {
    const off = this.on(eventName, payload => {
      off();
      listener(payload);
    });

    return off;
  }

  off<EventName extends WebSocketEventName>(
    eventName: EventName,
    listener: WebSocketListener<WebSocketClientEventMap<IncomingMessage>[EventName]>
  ) {
    this.listeners.get(eventName)?.delete(listener as WebSocketListener);
  }

  clearQueue() {
    this.messageQueue = [];
  }

  destroy() {
    this.close();
    this.clearQueue();
    this.listeners.clear();
    this.unbindNetworkEvents();
  }

  getStatus() {
    return this.status;
  }

  getSocket() {
    return this.socket;
  }

  getQueueSize() {
    return this.messageQueue.length;
  }

  getReconnectAttempts() {
    return this.reconnectAttempts;
  }

  private async resolveUrl() {
    if (this.options.getUrl) {
      return this.options.getUrl();
    }

    return this.options.url || '';
  }

  private parseMessage(event: MessageEvent) {
    const parser = this.options.parser || defaultParser<IncomingMessage>;
    return parser(event);
  }

  private serializeMessage(message: OutgoingMessage) {
    const serializer =
      this.options.serializer || defaultSerializer<OutgoingMessage>;
    return serializer(message);
  }

  private enqueueMessage(message: OutgoingMessage) {
    const maxQueueSize = this.options.maxQueueSize ?? DEFAULT_OPTIONS.maxQueueSize;
    this.messageQueue.push(message);

    if (this.messageQueue.length > maxQueueSize) {
      this.messageQueue = this.messageQueue.slice(-maxQueueSize);
    }
  }

  private flushQueue() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

    const queue = [...this.messageQueue];
    this.messageQueue = [];
    queue.forEach(message => this.send(message, { queue: false }));
  }

  private shouldReconnect(event?: CloseEvent) {
    if (this.closedByUser || !this.options.reconnect || !this.isOnline) {
      return false;
    }

    if (this.options.shouldReconnect && !this.options.shouldReconnect(event)) {
      return false;
    }

    const maxReconnectAttempts =
      this.options.maxReconnectAttempts ?? DEFAULT_OPTIONS.maxReconnectAttempts;
    return this.reconnectAttempts < maxReconnectAttempts;
  }

  private scheduleReconnect(event?: CloseEvent) {
    if (!this.shouldReconnect(event)) {
      this.setStatus('closed');
      return;
    }

    this.reconnectAttempts += 1;
    const delay = this.getReconnectDelay();
    this.setStatus('reconnecting');
    this.emit('reconnect', {
      attempt: this.reconnectAttempts,
      delay,
    });
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private getReconnectDelay() {
    const base = this.options.reconnectInterval ?? DEFAULT_OPTIONS.reconnectInterval;
    const decay = this.options.reconnectDecay ?? DEFAULT_OPTIONS.reconnectDecay;
    const max =
      this.options.maxReconnectInterval ?? DEFAULT_OPTIONS.maxReconnectInterval;
    const jitter = this.options.reconnectJitter ?? DEFAULT_OPTIONS.reconnectJitter;
    const delay = Math.min(
      base * Math.pow(decay, Math.max(this.reconnectAttempts - 1, 0)),
      max
    );
    const jitterOffset = delay * jitter * (Math.random() * 2 - 1);

    return Math.max(0, Math.round(delay + jitterOffset));
  }

  private startConnectTimeout(socket: WebSocket) {
    this.clearConnectTimeout();
    const timeout = this.options.connectTimeout ?? DEFAULT_OPTIONS.connectTimeout;
    if (timeout <= 0) return;

    this.connectTimeoutTimer = setTimeout(() => {
      if (socket !== this.socket || socket.readyState !== WebSocket.CONNECTING) {
        return;
      }

      socket.close();
    }, timeout);
  }

  private startHeartbeat() {
    this.clearHeartbeatTimer();
    if (!this.options.heartbeat) return;

    const interval =
      this.options.heartbeatInterval ?? DEFAULT_OPTIONS.heartbeatInterval;
    this.heartbeatTimer = setTimeout(() => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

      const heartbeatMessage =
        typeof this.options.heartbeatMessage === 'function'
          ? this.options.heartbeatMessage()
          : this.options.heartbeatMessage;

      if (heartbeatMessage !== undefined) {
        this.send(heartbeatMessage as OutgoingMessage, { queue: false });
      }
      this.startHeartbeatTimeout();
      this.startHeartbeat();
    }, interval);
  }

  private startHeartbeatTimeout() {
    this.clearHeartbeatTimeoutTimer();
    const timeout =
      this.options.heartbeatTimeout ?? DEFAULT_OPTIONS.heartbeatTimeout;
    if (timeout <= 0) return;

    this.heartbeatTimeoutTimer = setTimeout(() => {
      this.emit('heartbeatTimeout', undefined);
      if (this.socket) {
        this.socket.close();
      }
    }, timeout);
  }

  private resetHeartbeatTimeout() {
    this.clearHeartbeatTimeoutTimer();
  }

  private clearTimers() {
    this.clearConnectTimeout();
    this.clearHeartbeatTimer();
    this.clearHeartbeatTimeoutTimer();
  }

  private clearConnectTimeout() {
    if (!this.connectTimeoutTimer) return;

    clearTimeout(this.connectTimeoutTimer);
    this.connectTimeoutTimer = null;
  }

  private clearHeartbeatTimer() {
    if (!this.heartbeatTimer) return;

    clearTimeout(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private clearHeartbeatTimeoutTimer() {
    if (!this.heartbeatTimeoutTimer) return;

    clearTimeout(this.heartbeatTimeoutTimer);
    this.heartbeatTimeoutTimer = null;
  }

  private clearReconnectTimer() {
    if (!this.reconnectTimer) return;

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private setStatus(status: WebSocketStatus) {
    if (this.status === status) return;

    const previousStatus = this.status;
    this.status = status;
    this.emit('status', {
      previousStatus,
      status,
    });
  }

  private emit<EventName extends WebSocketEventName>(
    eventName: EventName,
    payload: WebSocketClientEventMap<IncomingMessage>[EventName]
  ) {
    this.listeners.get(eventName)?.forEach(listener => {
      listener(payload);
    });
  }

  private bindNetworkEvents() {
    if (typeof window === 'undefined') return;

    this.isOnline = window.navigator.onLine;
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private unbindNetworkEvents() {
    if (typeof window === 'undefined') return;

    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private handleOnline = () => {
    this.isOnline = true;

    if (
      !this.closedByUser &&
      this.options.reconnect &&
      this.status !== 'open' &&
      this.status !== 'connecting'
    ) {
      this.connect();
    }
  };

  private handleOffline = () => {
    this.isOnline = false;
    this.clearReconnectTimer();
  };

  private handleVisibilityChange = () => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') return;
    if (this.closedByUser || !this.options.reconnect) return;
    if (this.status === 'open' || this.status === 'connecting') return;

    this.connect();
  };

  private log(...args: unknown[]) {
    if (!this.options.debug) return;

    // eslint-disable-next-line no-console
    console.warn('[WebSocketClient]', ...args);
  }
}

